import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  LinearProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Dialog,
  Rating,
  Chip,
  CardActions
} from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FavoriteIcon from '@mui/icons-material/Favorite';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { endpoint } from '../resource/Constant';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ShoppingCartIcon, Search, Truck } from 'lucide-react';
import Header from './Header';
import { useNavigate } from 'react-router-dom';

const assessments = [
  {
    title: 'Behavioral Traits',
    description: 'Assess personality and behavior patterns',
    icon: <PsychologyIcon sx={{ fontSize: 50, color: '#f06292' }} />,
    color: 'primary'
  },
  {
    title: 'Interests & Preferences',
    description: 'Discover passions and favorite activities',
    icon: <FavoriteIcon sx={{ fontSize: 50, color: '#e91e63' }} />,
    color: 'secondary'
  },
  {
    title: 'Learning & Development Goals',
    description: 'Focus on developmental objectives',
    icon: <TrackChangesIcon sx={{ fontSize: 50, color: '#4caf50' }} />,
    color: 'success'
  }
];

const behavioralQuestions = [
  {
    question: 'How would you describe your child’s energy level?',
    options: [
      'High Energy (loves running, jumping, or active play)',
      'Medium Energy (likes structured play but also enjoys downtime)',
      'Low Energy (prefers calm activities, like sitting and building or solving puzzles)'
    ]
  },
  {
    question: 'Does your child enjoy playing with other children?',
    options: [
      'Yes (likes group play, socializing)',
      'Sometimes (engages in group play but sometimes prefers alone time)',
      'No (prefers solitary play, quiet activities)'
    ]
  },
  {
    question: 'How would you describe your child’s temperament?',
    options: [
      'Aggressive (likes to roughhouse or engage in competitive play)',
      'Gentle (prefers calm activities and is more nurturing)',
      'Balanced (varies between both types)'
    ]
  },
  {
    question: 'What does your child do when they are upset?',
    options: [
      'Throws things around (reacts physically, likes to express frustration through movement)',
      'Becomes quiet (withdraws, needs comfort or space)',
      'Seeks others (finds comfort in people, wants attention)'
    ]
  },
  {
    question: 'Does your child enjoy sorting or organizing objects?',
    options: [
      'Yes (likes putting things in order, tidying up)',
      'Sometimes (doesn’t mind organizing when prompted)',
      'No (doesn’t show much interest in organization)'
    ]
  }
];

const interestsQuestions = [
  {
    question: 'What is your child most interested in?',
    options: [
      'Animals (loves playing with animal figures, talking about animals, etc.)',
      'Vehicles (loves cars, trucks, trains, etc.)',
      'Nature (enjoys outdoor activities, plants, and nature exploration)',
      'Art (enjoys drawing, painting, crafts)',
      'Music (enjoys singing, dancing, or playing musical instruments)'
    ]
  },
  {
    question: 'What type of activities does your child enjoy the most?',
    options: [
      'Active play (running, jumping, climbing, etc.)',
      'Quiet play (building with blocks, reading, solving puzzles)',
      'Creative play (drawing, painting, dress-up, roleplay)',
      'Cognitive challenges (solving puzzles, educational games)'
    ]
  }
];

const learningQuestions = [
  {
    question: 'What is your primary goal for the toys you want for your child?',
    options: [
      'Learning (e.g., educational games, language development, STEM toys)',
      'Emotional development (toys that encourage empathy, nurturing, social interaction)',
      'Physical activity (toys that promote movement, coordination, and strength)',
      'Creativity (toys that foster imagination and creative expression)',
      'Speech development (toys focused on words, sounds, and communication)'
    ]
  },
  {
    question: 'Does your child need help with any of the following?',
    options: [
      'Learning to share or cooperate (social development)',
      'Focus and attention (concentration, patience)',
      'Fine motor skills (building, sorting, drawing)',
      'Problem-solving skills (puzzles, matching games)'
    ]
  }
];

export default function ToyRecommendation() {
  const [buttons, setButtons] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [loader, setLoader] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [products, setProducts] = useState([]);
  const [home , setHome] = useState(false);
  const navigate = useNavigate();

  const handleAnswer = (event) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = event.target.value;
    setAnswers(newAnswers);
  };

  const get_recommendation = () => {
    setLoader(true);
    axios
      .post(
        endpoint + '/api/get-recommendation',
        { query: answers },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('recommendationenginetoken')}`
            }
        }
      )
      .then((res) => {
        setProducts(res?.data?.data?.recommendation);
        setLoader(false);
      })
      .catch((error) => {
        setLoader(false);
        setSnackbar({
          open: true,
          message: error?.response?.data?.message || 'An unexpected errror occured',
          severity: 'error'
        });
        if (error?.status == 401 || error?.status  == 402) {
          localStorage.removeItem('recommendationenginetoken');
          setTimeout(() => {
            navigate('/recommendation/login')
          }, 1000)
        } {

        }
      });
  };

  const handleNext = () => {
    let equation;
    if (buttons === 'Behavioral Traits') {
      equation = currentQuestion < behavioralQuestions.length - 1;
    } else if (buttons === 'Interests & Preferences') {
      equation = currentQuestion < interestsQuestions.length - 1;
    } else {
      equation = currentQuestion < learningQuestions.length - 1;
    }

    if (equation) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      get_recommendation();
    }
  };

  const handleButton = (buttonName) => {
    setButtons(buttonName);
  };

  const handleBack = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setButtons('');
    setHome(true)
  };

  const handleHomeButton = () => {
    window.location.reload()
  }

  return (
    <>
      <Header />
      <Box
        sx={{
          p: 5,
          textAlign: 'center',
          backgroundColor: 'rgb(255, 255, 255)',
          minHeight: '100vh'
        }}
      >
        {!buttons && (
          <>
            <Typography marginTop="30px" variant="h4" color="primary" fontWeight="bold" gutterBottom>
              Toy Recommendation Engine
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Help us find the perfect toys for your child by completing our interactive assessments.
              <br />
              Each category helps us understand your child’s unique personality and needs.
            </Typography>
          </>
        )}

        {!buttons && (
          <Grid container spacing={4} justifyContent="center">
            {assessments.map((item, index) => (
              <Grid item xs={12} sm={6} marginTop="60px" md={4} key={index}>
                <Card
                  elevation={3}
                  sx={{
                    width: '400px',
                    height: '300px',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    backgroundColor: 'rgb(227, 242, 255)',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                      backgroundColor: '#ffffff',
                      border: '2px solid #2196f3',
                      backgroundColor: 'rgb(227, 242, 255)'
                    }
                  }}
                >
                  <CardContent>
                    <Box marginTop="30px" sx={{ mb: 2 }}>
                      {item.icon}
                    </Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3 }}>
                      {item.description}
                    </Typography>
                    <Button onClick={() => handleButton(item.title)} variant="contained" color={item.color}>
                      Start
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        {!products.length != 0 && buttons === 'Behavioral Traits' && (
          <Box marginTop="30px" maxWidth="md" mx="auto">
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
                Back to Categories
              </Button>
            </Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Behavioral Traits
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              These questions help assess the child’s personality and behavior, which will influence the type of toys that might suit their
              playstyle and needs.
            </Typography>
            <Typography sx={{ mb: 1 }}>
              Question {currentQuestion + 1} of {behavioralQuestions.length}
            </Typography>
            <LinearProgress variant="determinate" value={((currentQuestion + 1) * 100) / behavioralQuestions.length} sx={{ mb: 3 }} />
            <Card variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                {behavioralQuestions[currentQuestion].question}
              </Typography>
              <RadioGroup value={answers[currentQuestion] || ''} onChange={handleAnswer}>
                {behavioralQuestions[currentQuestion].options.map((option, index) => (
                  <FormControlLabel key={index} value={option} control={<Radio />} label={option} />
                ))}
              </RadioGroup>
            </Card>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" onClick={handleNext} disabled={!answers[currentQuestion]}>
                {currentQuestion + 1 === behavioralQuestions.length ? 'Get Recommendation' : 'Next Question'}
              </Button>
            </Box>
          </Box>
        )}

        {!products.length != 0 && buttons === 'Interests & Preferences' && (
          <Box marginTop="30px" maxWidth="md" mx="auto">
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
                Back to Categories
              </Button>
            </Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Interests & Preferences
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              These questions help assess the child’s personality and behavior, which will influence the type of toys that might suit their
              playstyle and needs.
            </Typography>
            <Typography sx={{ mb: 1 }}>
              Question {currentQuestion + 1} of {interestsQuestions.length}
            </Typography>
            <LinearProgress variant="determinate" value={((currentQuestion + 1) * 100) / interestsQuestions.length} sx={{ mb: 3 }} />
            <Card variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                {interestsQuestions[currentQuestion].question}
              </Typography>
              <RadioGroup value={answers[currentQuestion] || ''} onChange={handleAnswer}>
                {interestsQuestions[currentQuestion].options.map((option, index) => (
                  <FormControlLabel key={index} value={option} control={<Radio />} label={option} />
                ))}
              </RadioGroup>
            </Card>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" onClick={handleNext} disabled={!answers[currentQuestion]}>
                {currentQuestion + 1 === interestsQuestions.length ? 'Get Recommendation' : 'Next Question'}
              </Button>
            </Box>
          </Box>
        )}

        {!products.length != 0 && buttons === 'Learning & Development Goals' && (
          <Box marginTop="30px" maxWidth="md" mx="auto">
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button onClick={handleBack} variant="outlined" startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
                Back to Categories
              </Button>
            </Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Learning & Development Goals
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              These questions help assess the child’s personality and behavior, which will influence the type of toys that might suit their
              playstyle and needs.
            </Typography>
            <Typography sx={{ mb: 1 }}>
              Question {currentQuestion + 1} of {learningQuestions.length}
            </Typography>
            <LinearProgress variant="determinate" value={((currentQuestion + 1) * 100) / learningQuestions.length} sx={{ mb: 3 }} />
            <Card variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                {learningQuestions[currentQuestion].question}
              </Typography>
              <RadioGroup value={answers[currentQuestion] || ''} onChange={handleAnswer}>
                {learningQuestions[currentQuestion].options.map((option, index) => (
                  <FormControlLabel key={index} value={option} control={<Radio />} label={option} />
                ))}
              </RadioGroup>
            </Card>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" onClick={handleNext} disabled={!answers[currentQuestion]}>
                {currentQuestion + 1 === learningQuestions.length ? 'Get Recommendation' : 'Next Question'}
              </Button>
            </Box>
          </Box>
        )}

        {products.length != 0 &&(
          <Box marginTop="10px" sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" position="relative">
              <Box position="absolute" left={0}>
                <Button onClick={handleHomeButton} variant='contained' >Get back to home</Button>
              </Box>

              <Box flexGrow={1} textAlign="center">
                <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                  Your Personalized Toy Recommendations
                </Typography>
              </Box>
            </Box>
            <Typography variant="body1" align="center" color="text.secondary" mb={4}>
              Based on your child's unique profile, we've selected these perfect toys that match their interests, behavior, and
              developmental goals.
            </Typography>

            <Grid display={'flex'} justifyContent={'space-around'} container spacing={4}>
              {products.map((product) => (
                <Grid item xs={12} sm={6} md={4} key={product.id}>
                  <Card elevation={3} sx={{ textAlign: 'center', height: '100%' }}>
                    <CardContent>
                      <Typography variant="h2" component="div" mb={1}>
                        {product.icon}
                      </Typography>
                      <Typography variant="h6" gutterBottom>
                        {product.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        {product.description}
                      </Typography>
                      <Box display="flex" justifyContent="center" alignItems="center" mb={1}>
                        <Rating value="4.5" precision={0.1} readOnly />
                        <Typography variant="body2" color="text.secondary" ml={1}>
                          ({product.rating})
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="success.main" mb={2}>
                        ₹{product.price.toFixed(2)}
                      </Typography>
                      <Box display="flex" justifyContent="center" flexWrap="wrap" gap={1} mb={2}>
                        {product.benefits.map((benefit, i) => (
                          <Chip key={i} label={benefit} color="secondary" size="small" />
                        ))}
                      </Box>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                      <a href={product.link} target="_black" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                        <Button variant="contained" startIcon={<Search />}>
                          Visit
                        </Button>
                      </a>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {/* <Box display={'flex'} justifyContent={'flex-end'}>
            <Button variant='contained'>
              Try another
            </Button>
          </Box> */}
          </Box>
        )}

        <Backdrop open={loader}>
          <svg width={0} height={0}>
            <defs>
              <linearGradient id="my_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#4169E1" />
                <stop offset="30%" stopColor="#f5f5f5" />
                <stop offset="100%" stopColor="#fff" />
              </linearGradient>
            </defs>
          </svg>
          <CircularProgress sx={{ 'svg circle': { stroke: 'url(#my_gradient)' } }} size={70} thickness={3} />
        </Backdrop>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{
              width: '100%',
              backgroundColor: '#0D47A1',
              color: '#fff',
              fontWeight: 'bold'
            }}
          >
            <div className="flex items-center gap-2">
              {snackbar.message}
              <span className="animate-bounce">.</span>
              <span className="animate-bounce delay-200">.</span>
              <span className="animate-bounce delay-400">.</span>
            </div>
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
}
